<template>
    <v-col
      cols="12"
      v-if="currentAttributeMap"
    >
        <v-col
          cols="12"
          v-if="currentFacilityAttribute === defaultTypeId"
        >
            <p>
                The 'default' unit type allows you to set the value that will be used if no value is set for a
                specific unit type. This allows you to use a different value than the global default for an attribute
                for all unit types in this facility. To set a value for a specific unit type, use the select box above
                to switch to that unit type.
            </p>
        </v-col>
        <v-row v-if="currentFacilityAttribute !== defaultTypeId">
            <v-col cols="12">
                <v-switch
                    data-testid="unit-attribute-enabled-switch"
                    class="unit-attribute-enabled-switch"
                    label="Enabled"
                    v-model="currentAttributeMap.enabled"
                />
            </v-col>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <app-input
                    data-testid="unit-attribute-name-input"
                    class="unit-attribute-name-input"
                    :type="'text'"
                    label="'Unit Type Display Name'"
                    v-model.trim="currentAttributeMap.name"
                    :hint="'Leave blank to use the Unit Type name from SiteLink'"
                />
            </v-col>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <v-select
                    data-testid="unit-attribute-product-type-select"
                    class="unit-attribute-product-type-select"
                    label="Unit Type Is"
                    v-model="currentAttributeMap.productType"
                    :items="productTypes"
                    clearable
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <v-switch
                    data-testid="unit-attribute-alarm-switch"
                    class="unit-attribute-alarm-switch"
                    v-model="currentAttributeMap.alarm.enabled"
                    label="Alarm"
                />
                <transition name="scroll-y-transition" mode="out-in">
                    <v-row v-if="currentAttributeMap.alarm.enabled">
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-alarm-on-input"
                                class="unit-attribute-alarm-on-input"
                                :type="'text'"
                                label="Display Value (on)"
                                v-model.trim="currentAttributeMap.alarm.on_value"
                            />
                        </v-col>
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-alarm-off-input"
                                class="unit-attribute-alarm-off-input"
                                :type="'text'"
                                label="Display Value (off)"
                                v-model.trim="currentAttributeMap.alarm.off_value"
                            />
                        </v-col>
                    </v-row>
                </transition>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <v-switch
                    data-testid="unit-attribute-climate-switch"
                    class="unit-attribute-climate-switch"
                    v-model="currentAttributeMap.climate.enabled"
                    label="Climate"
                />
                <transition
                  name="scroll-y-transition"
                  mode="out-in"
                >
                    <v-row v-if="currentAttributeMap.climate.enabled">
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-climate-on-input"
                                class="unit-attribute-climate-on-input"
                                :type="'text'"
                                label="Display Value (on)"
                                v-model.trim="currentAttributeMap.climate.on_value"
                            />
                        </v-col>
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-climate-off-input"
                                class="unit-attribute-climate-off-input"
                                :type="'text'"
                                label="Display Value (off)"
                                v-model.trim="currentAttributeMap.climate.off_value"
                            />
                        </v-col>
                    </v-row>
                </transition>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <v-switch
                    data-testid="unit-attribute-mobile-switch"
                    class="unit-attribute-mobile-switch"
                    v-model="currentAttributeMap.mobile.enabled"
                    label="Mobile"
                />
                <transition
                  name="scroll-y-transition"
                  mode="out-in"
                >
                    <v-row v-if="currentAttributeMap.mobile.enabled">
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-mobile-on-input"
                                class="unit-attribute-mobile-on-input"
                                :type="'text'"
                                label="Display Value (on)"
                                v-model.trim="currentAttributeMap.mobile.on_value"
                            />
                        </v-col>
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-mobile-off-input"
                                class="unit-attribute-mobile-off-input"
                                :type="'text'"
                                label="Display Value (off)"
                                v-model.trim="currentAttributeMap.mobile.off_value"
                            />
                        </v-col>
                    </v-row>
                </transition>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <v-switch
                    data-testid="unit-attribute-power-switch"
                    class="unit-attribute-power-switch"
                    v-model="currentAttributeMap.power.enabled"
                    label="Power"
                />
                <transition name="scroll-y-transition" mode="out-in">
                    <v-row v-if="currentAttributeMap.power.enabled">
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-power-on-input"
                                class="unit-attribute-power-on-input"
                                :type="'text'"
                                label="Display Value (on)"
                                v-model.trim="currentAttributeMap.power.on_value"
                            />
                        </v-col>
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-power-off-input"
                                class="unit-attribute-power-off-input"
                                :type="'text'"
                                label="Display Value (off)"
                                v-model.trim="currentAttributeMap.power.off_value"
                            />
                        </v-col>
                    </v-row>
                </transition>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              lg="4"
            >
                <v-switch
                    data-testid="unit-attribute-inside-switch"
                    class="unit-attribute-inside-switch"
                    v-model="currentAttributeMap.inside.enabled"
                    label="Inside"
                />
                <transition
                  name="scroll-y-transition"
                  mode="out-in"
                >
                    <v-row v-if="currentAttributeMap.inside.enabled">
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-inside-on-input"
                                class="unit-attribute-inside-on-input"
                                :type="'text'"
                                label="Display Value (on)"
                                v-model.trim="currentAttributeMap.inside.on_value"
                            />
                        </v-col>
                        <v-col cols="6">
                            <app-input
                                data-testid="unit-attribute-inside-off-input"
                                class="unit-attribute-inside-off-input"
                                :type="'text'"
                                label="Display Value (off)"
                                v-model.trim="currentAttributeMap.inside.off_value"
                            />
                        </v-col>
                    </v-row>
                </transition>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
                <v-switch
                    data-testid="unit-attribute-floor-switch"
                    class="unit-attribute-floor-switch"
                    v-model="currentAttributeMap.floor.enabled"
                    label="Floor"
                />
                <transition
                  name="scroll-y-transition"
                  mode="out-in"
                >
                    <v-row v-if="currentAttributeMap.floor.enabled">
                        <v-col cols="4">
                            <app-input
                                data-testid="unit-attribute-zero-value-input"
                                class="unit-attribute-zero-value-input"
                                :type="'text'"
                                label="Floor = 0"
                                v-model.trim="currentAttributeMap.floor.zero_value"
                            />
                        </v-col>
                        <v-col cols="4">
                            <app-input
                                data-testid="unit-attribute-one-value-input"
                                class="unit-attribute-one-value-input"
                                :type="'text'"
                                label="Floor = 1"
                                v-model.trim="currentAttributeMap.floor.one_value"
                            />
                        </v-col>
                        <v-col cols="4">
                            <app-input
                                data-testid="unit-attribute-two-value-input"
                                class="unit-attribute-two-value-input"
                                :type="'text'"
                                label="Floor = 2+"
                                v-model.trim="currentAttributeMap.floor.two_plus_value"
                            />
                        </v-col>
                    </v-row>
                </transition>
            </v-col>
        </v-row>
    </v-col>
</template>

<script>
export default {
  data() {
    return {
      defaultTypeId: 99999999
    };
  },

  props: {
    currentAttributeMap: Object,
    productTypes: Array,
    currentFacilityAttribute: Number
  }
};
</script>
